import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Stack,
  IconButton,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  BugReport as BugIcon,
  Build as BuildIcon,
  Lightbulb as FeatureIcon,
  PriorityHigh as PriorityHighIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

const TicketEditModal = ({ open, onClose, ticket, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: ticket?.title || '',
    description: ticket?.description || '',
    type: ticket?.type || 'Bug',
    priority: ticket?.priority || 'Medium',
    status: ticket?.status || 'Open',
    dueDate: ticket?.dueDate || ''
  });

  const [errors, setErrors] = useState({
    title: '',
    description: '',
    type: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleSubmit = () => {
    let valid = true;
    const newErrors = { ...errors };

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
      valid = false;
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
      valid = false;
    }

    setErrors(newErrors);

    if (valid) {
      onSubmit(formData);
    }
  };

  if (!ticket) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Ticket</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Title *"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={!!errors.title}
            helperText={errors.title}
          />

          <TextField
            fullWidth
            label="Description *"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={4}
            error={!!errors.description}
            helperText={errors.description}
          />

          <FormControl fullWidth>
            <InputLabel>Type *</InputLabel>
            <Select
              name="type"
              value={formData.type}
              label="Type *"
              onChange={handleChange}
            >
              <MenuItem value="Bug">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <BugIcon color="error" />
                  <span>Bug</span>
                </Stack>
              </MenuItem>
              <MenuItem value="Feature Request">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FeatureIcon color="primary" />
                  <span>Feature Request</span>
                </Stack>
              </MenuItem>
              <MenuItem value="Technical Issue">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <BuildIcon color="action" />
                  <span>Technical Issue</span>
                </Stack>
              </MenuItem>
              <MenuItem value="Other">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AssignmentIcon />
                  <span>Other</span>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              name="priority"
              value={formData.priority}
              label="Priority"
              onChange={handleChange}
            >
              <MenuItem value="Low">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PriorityHighIcon />
                  <span>Low</span>
                </Stack>
              </MenuItem>
              <MenuItem value="Medium">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PriorityHighIcon color="warning" />
                  <span>Medium</span>
                </Stack>
              </MenuItem>
              <MenuItem value="High">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PriorityHighIcon color="error" />
                  <span>High</span>
                </Stack>
              </MenuItem>
              <MenuItem value="Critical">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PriorityHighIcon color="error" />
                  <span>Critical</span>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              label="Status"
              onChange={handleChange}
            >
              <MenuItem value="Open">
                <Chip label="Open" size="small" />
              </MenuItem>
              <MenuItem value="In Progress">
                <Chip label="In Progress" color="primary" size="small" />
              </MenuItem>
              <MenuItem value="Resolved">
                <Chip label="Resolved" color="success" size="small" />
              </MenuItem>
              <MenuItem value="Closed">
                <Chip label="Closed" size="small" />
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Due Date"
            name="dueDate"
            type="date"
            value={formData.dueDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          startIcon={<CloseIcon />}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          startIcon={<CheckIcon />}
          sx={{ borderRadius: 2 }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketEditModal;